import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, Observable } from 'rxjs';
import * as FileUtils from '../../../utils/file/file-utils';
import { MACHINE_LEARNING_SERVICE_URL } from '../../shared/constants/constants';
import { retry } from '../decorators/retry';
import { DatasetFileTypeOption } from '../enums/dataset-file-type-option.enum';
import { UploadPayload } from '../models';
import { DatasetFilePayload, DatasetFileResponse } from '../models/dataset.model';
import { FileModel } from '../models/file-model.model';
import { UploadInfo } from '../models/upload-info.model';
import { BlobParameter } from '../types/blob-parameter.type';
import { FileService } from './file.service';

@Injectable({
    providedIn: 'root',
})
export class FileUploadService {
    uploaderStore = new Map<string, FileUploader>();
    private readonly FILE_TO_DATASET_API_URL = `${MACHINE_LEARNING_SERVICE_URL}/datasets/uploads/files`;
    private readonly ANNOTATION_TO_DATASET_API_URL = `${MACHINE_LEARNING_SERVICE_URL}/datasets/uploads/externalannotations`;
    private _initialValueOfFilesToBeUploaded$$: BehaviorSubject<number> = new BehaviorSubject(0);
    initialValueOfFilesToBeUploaded$ = this._initialValueOfFilesToBeUploaded$$.asObservable();

    constructor(
        private http: HttpClient,
        private fileService: FileService,
    ) {}

    isAnyUploadInProgress(): boolean {
        return Array.from(this.uploaderStore.values()).some((uploader: FileUploader) => uploader.isUploading);
    }

    @retry()
    createDbEntryForDatasetFile$(name: string, datasetId: string, datasetFileType: DatasetFileTypeOption): Observable<DatasetFileResponse> {
        const payload: DatasetFilePayload = {
            datasetId,
            fileName: name,
        };
        return this.http.post(
            datasetFileType === DatasetFileTypeOption.ANNOTATIONS ? this.ANNOTATION_TO_DATASET_API_URL : this.FILE_TO_DATASET_API_URL,
            payload,
        ) as Observable<DatasetFileResponse>;
    }

    addToUploaderCache(key: string, uploader: FileUploader): void {
        if (this.uploaderStore.get(key) == null) {
            this.uploaderStore.set(key, uploader);
        }
    }

    fetchFromCache(key: string): FileUploader | null {
        let uploader: FileUploader | null = null;
        if (this.uploaderStore.get(key) != null) {
            uploader = this.uploaderStore.get(key)!;
        }
        return uploader;
    }

    setInitialValueOfFiles(totalFiles: number): void {
        this._initialValueOfFilesToBeUploaded$$.next(totalFiles);
    }

    resetInitialValueOfFiles(): void {
        return this._initialValueOfFilesToBeUploaded$$.next(0);
    }

    loadInitialValueOfFiles(): number {
        return this._initialValueOfFilesToBeUploaded$$.value;
    }

    postBlob$(blobParameter: BlobParameter): Observable<UploadInfo> {
        return this.http.post<UploadInfo>(`/api/documents/upload/blob`, blobParameter);
    }

    postRdp$(payload: UploadPayload): Observable<UploadPayload> {
        return this.http.post<UploadPayload>(`/api/documents/rdp/files/submit`, payload);
    }

    saveFileInDb$(fileItem: FileItem, signFileUrl: boolean = false): Observable<FileModel> {
        return this.fileService.addFile$(fileItem, signFileUrl);
    }

    move$(targetFolderId: string, fileIds: string[], folderIds?: string[]): Observable<void> {
        return this.fileService.move$(targetFolderId, fileIds, folderIds);
    }

    sortStrings(file1Location: string, file2Location: string, order: 'asc' | 'desc'): number {
        const f1Name = FileUtils.getFileNameWithExtension(file1Location).toLowerCase();
        const f2Name = FileUtils.getFileNameWithExtension(file2Location).toLowerCase();
        if (order === 'desc') {
            return f1Name < f2Name ? 1 : f1Name === f2Name ? 0 : -1;
        } else {
            return f1Name > f2Name ? 1 : f1Name === f2Name ? 0 : -1;
        }
    }
}
